import {useCallback, useEffect, useRef, useState} from 'react';
import {Client, IFrame, IMessage} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

type Message = {
    guid: string;
    conversationGuid: string;
    senderGuid: string;
    internal: boolean;
    messageType: 'TEXT' | 'URGENT_FLAG_ON' | 'URGENT_FLAG_OFF' | 'OFFER_CANCELLED' | 'OFFER_DECLINED' | 'OFFER_UNMATCHED' | 'OFFER_MATCHED';
    content: string;
    createdAt: string;
};

export type ReadReceipt = {
    conversationGuid: string;
    userGuid: string;
    internal: boolean;
    lastReadMessageGuid: string | null;
    lastReadAt: string;
};

export type OfferConversationSummaryDTO = {
    offerGuid: string;
    hasConversation: boolean;
    urgent: boolean;
    providerUnreadCount: number;
    clientPlacementTeamUnreadCount: number;
};

const SOCKET_PATH = '/api/ws/messaging';

// STOMP payloads may carry Instant values as decimal epoch-seconds depending on the backend's
// message converter configuration; normalize to ISO strings so new Date(...) parses correctly.
const toIsoTimestamp = (value: unknown): string => {
    if (typeof value === 'number') {
        return new Date(value * 1000).toISOString();
    }
    return String(value);
};

function getSocketUrl(apiUrl: string): string {
    if (!apiUrl) {
        return SOCKET_PATH;
    }
    return `${apiUrl}${SOCKET_PATH}`;
}

type MessageHandler = (body: unknown) => void;

class StompClientManager {
    private client: Client | null = null;
    private token: string | null = null;
    private userCount = 0;
    private connected = false;
    private connectionListeners = new Set<(connected: boolean) => void>();
    private handlersByTopic = new Map<string, Set<MessageHandler>>();
    private stompSubscriptions = new Map<string, {unsubscribe: () => void}>();

    register(token: string): void {
        this.userCount++;
        if (this.token !== token) {
            this.token = token;
            if (this.client?.active) {
                this.client.deactivate();
            }
            this.client = null;
        }
        this.ensureClient();
    }

    unregister(): void {
        this.userCount = Math.max(0, this.userCount - 1);
        if (this.userCount === 0 && this.client?.active) {
            this.client.deactivate();
            this.client = null;
        }
    }

    disconnect(): void {
        if (this.client?.active) {
            this.client.deactivate();
        }
        this.client = null;
        this.userCount = 0;
        this.token = null;
        this.stompSubscriptions.clear();
        this.setConnected(false);
    }

    addConnectionListener(listener: (connected: boolean) => void): void {
        this.connectionListeners.add(listener);
    }

    removeConnectionListener(listener: (connected: boolean) => void): void {
        this.connectionListeners.delete(listener);
    }

    subscribe(topic: string, handler: MessageHandler): void {
        const handlers = this.handlersByTopic.get(topic) ?? new Set<MessageHandler>();
        handlers.add(handler);
        this.handlersByTopic.set(topic, handlers);
        if (this.client?.connected) {
            this.createStompSubscription(topic);
        }
    }

    unsubscribe(topic: string, handler: MessageHandler): void {
        const handlers = this.handlersByTopic.get(topic);
        if (!handlers) return;
        handlers.delete(handler);
        if (handlers.size === 0) {
            this.handlersByTopic.delete(topic);
            const stompSubscription = this.stompSubscriptions.get(topic);
            if (stompSubscription) {
                stompSubscription.unsubscribe();
                this.stompSubscriptions.delete(topic);
            }
        }
    }

    isConnected(): boolean {
        return this.connected;
    }

    private ensureClient(): void {
        if (this.client?.active) {
            return;
        }
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const createdClient = new Client({
            webSocketFactory: () => new SockJS(getSocketUrl(apiUrl)) as unknown as WebSocket,
            connectHeaders: {
                Authorization: `Bearer ${this.token}`,
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 0,
            heartbeatOutgoing: 10000,
            onConnect: () => {
                if (this.client !== createdClient) return;
                this.handlersByTopic.forEach((_, topic) => this.createStompSubscription(topic));
                this.setConnected(true);
            },
            onDisconnect: () => {
                if (this.client !== createdClient) return;
                this.stompSubscriptions.clear();
                this.setConnected(false);
            },
            onStompError: (frame: IFrame) => {
                if (this.client !== createdClient) return;
                console.error('STOMP error', frame);
            },
            onWebSocketClose: (event: CloseEvent) => {
                if (this.client !== createdClient) return;
                console.warn('STOMP WebSocket closed', event.code, event.reason);
                this.stompSubscriptions.clear();
                this.setConnected(false);
            },
            onWebSocketError: (event: Event) => {
                if (this.client !== createdClient) return;
                console.error('STOMP WebSocket error', event);
            },
        });
        createdClient.activate();
        this.client = createdClient;
    }

    private createStompSubscription(topic: string): void {
        if (!this.client?.connected || this.stompSubscriptions.has(topic)) {
            return;
        }
        const handlers = this.handlersByTopic.get(topic);
        if (!handlers || handlers.size === 0) {
            return;
        }
        const subscription = this.client.subscribe(topic, (message: IMessage) => {
            const body = JSON.parse(message.body);
            handlers.forEach((handler) => handler(body));
        });
        this.stompSubscriptions.set(topic, subscription);
    }

    private setConnected(connected: boolean): void {
        if (this.connected === connected) {
            return;
        }
        this.connected = connected;
        this.connectionListeners.forEach((listener) => listener(connected));
    }
}

export {StompClientManager};

const stompManager = new StompClientManager();

if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        stompManager.disconnect();
    });
}

export function useStompClient(token: string | null | undefined, enabled: boolean = true) {
    const [connected, setConnected] = useState(stompManager.isConnected());

    useEffect(() => {
        if (!token || !enabled) {
            return;
        }
        stompManager.register(token);
        setConnected(stompManager.isConnected());
        const listener = (isConnected: boolean) => setConnected(isConnected);
        stompManager.addConnectionListener(listener);
        return () => {
            stompManager.removeConnectionListener(listener);
            stompManager.unregister();
        };
    }, [token, enabled]);

    const subscribe = useCallback((topic: string, handler: MessageHandler) => {
        stompManager.subscribe(topic, handler);
    }, []);

    const unsubscribe = useCallback((topic: string, handler: MessageHandler) => {
        stompManager.unsubscribe(topic, handler);
    }, []);

    return {connected, subscribe, unsubscribe};
}

export function useSharedMessaging(token: string | null | undefined, conversationGuid: string | null) {
    const {connected, subscribe, unsubscribe} = useStompClient(token, Boolean(conversationGuid));
    const [messages, setMessages] = useState<Message[]>([]);
    const [readReceipts, setReadReceipts] = useState<ReadReceipt[]>([]);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!conversationGuid) {
            return;
        }
        const messageTopic = `/topic/conversation/${conversationGuid}/messages`;
        const readReceiptTopic = `/topic/conversation/${conversationGuid}/read-receipts`;
        const messageHandler = (payload: unknown) => {
            if (!isMountedRef.current) {
                return;
            }
            const newMessage = payload as Message;
            newMessage.createdAt = toIsoTimestamp(newMessage.createdAt);
            setMessages((prev) => {
                if (prev.some((message) => message.guid === newMessage.guid)) {
                    return prev;
                }
                return [...prev, newMessage];
            });
        };
        const readReceiptHandler = (payload: unknown) => {
            if (!isMountedRef.current) {
                return;
            }
            const receipt = payload as ReadReceipt;
            receipt.lastReadAt = toIsoTimestamp(receipt.lastReadAt);
            setReadReceipts((prev) => [
                ...prev.filter((item) => item.userGuid !== receipt.userGuid),
                receipt,
            ]);
        };
        subscribe(messageTopic, messageHandler);
        subscribe(readReceiptTopic, readReceiptHandler);
        return () => {
            unsubscribe(messageTopic, messageHandler);
            unsubscribe(readReceiptTopic, readReceiptHandler);
        };
    }, [conversationGuid, subscribe, unsubscribe]);

    return {connected, messages, setMessages, readReceipts, setReadReceipts};
}

export function useOfferConversationSummary(offerGuid: string, token: string | null | undefined) {
    const {subscribe, unsubscribe} = useStompClient(token);
    const [summary, setSummary] = useState<OfferConversationSummaryDTO | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const topic = `/topic/offer/${offerGuid}/conversation-summary`;
        const handler = (payload: unknown) => {
            if (!isMountedRef.current) {
                return;
            }
            setSummary(payload as OfferConversationSummaryDTO);
        };
        subscribe(topic, handler);
        return () => {
            unsubscribe(topic, handler);
        };
    }, [offerGuid, subscribe, unsubscribe]);

    return summary;
}

function useUnreadConversationCountTopic(topic: string | null, token: string | null | undefined, enabled: boolean = true) {
    const {subscribe, unsubscribe} = useStompClient(token, enabled && Boolean(topic));
    const [count, setCount] = useState<number | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!enabled || !topic) {
            return;
        }
        const handler = (payload: unknown) => {
            if (!isMountedRef.current) {
                return;
            }
            setCount(payload as number);
        };
        subscribe(topic, handler);
        return () => {
            unsubscribe(topic, handler);
        };
    }, [topic, enabled, subscribe, unsubscribe]);

    return count;
}

export function useInternalUnreadConversationCount(token: string | null | undefined, enabled: boolean = true) {
    return useUnreadConversationCountTopic('/topic/unread-count/internal', token, enabled);
}

export function useProviderUnreadConversationCount(providerGuid: string | null | undefined, token: string | null | undefined, enabled: boolean = true) {
    return useUnreadConversationCountTopic(providerGuid ? `/topic/unread-count/provider/${providerGuid}` : null, token, enabled && Boolean(providerGuid));
}

export function useReferralConversationSummaries(referralGuid: string, token: string | null | undefined, enabled: boolean = true) {
    const {subscribe, unsubscribe} = useStompClient(token, enabled);
    const [summaries, setSummaries] = useState<OfferConversationSummaryDTO[] | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        const topic = `/topic/referral/${referralGuid}/offer-conversation-summaries`;
        const handler = (payload: unknown) => {
            if (!isMountedRef.current) {
                return;
            }
            setSummaries(payload as OfferConversationSummaryDTO[]);
        };
        subscribe(topic, handler);
        return () => {
            unsubscribe(topic, handler);
        };
    }, [referralGuid, enabled, subscribe, unsubscribe]);

    return summaries;
}
